/**
 * Fair Home V2 — H10 persona wiring
 * Maps auth + `/v1/personalization/status` → Capsules / Selected / AI copy states
 * Score comes from the server — never recompute.
 *
 * Note: auth-mismatch (token + anonymous-shaped status) is byte-identical to a
 * real newcomer — see fair-search-v2-auth-mismatch.data.ts. Home prefers the
 * newcomer pitch for that shape; Search hides the quiz CTA instead.
 */
import type { PersonalizationStatusResponse } from '@/features/search/api/searchV1'
import type { FairHomeV2AiPersonaId } from './fair-home-v2-ai.data'
import type { FairHomeV2CapsulesPersonaId } from './fair-home-v2-capsules.data'
import type { FairHomeV2SelectedPersonaId } from './fair-home-v2-selected.data'

export type FairHomeV2SectionLinkAction = 'auth' | 'taste' | 'profile'

export interface FairHomeV2PersonaState {
  capsules: FairHomeV2CapsulesPersonaId
  selected: FairHomeV2SelectedPersonaId
  ai: FairHomeV2AiPersonaId
  /** Server personalization score 0..100 — Capsules match + AI completeness */
  score: number
  sectionLinkAction: FairHomeV2SectionLinkAction | null
}

const GUEST: FairHomeV2PersonaState = {
  capsules: 'guest',
  selected: 'guest',
  ai: 'guest',
  score: 0,
  sectionLinkAction: 'auth',
}

/**
 * Resolve Home v2 personas from auth + personalization status.
 *
 * - Guest: no token
 * - next_action null → profile complete
 * - next_action style_quiz → newcomer
 * - measurements → explorer / registered-partial
 * - interactions | search_history → advanced / registered-partial
 */
export function resolveFairHomeV2PersonaState(input: {
  isAuthenticated: boolean
  status: PersonalizationStatusResponse | null | undefined
}): FairHomeV2PersonaState {
  if (!input.isAuthenticated) return GUEST

  const status = input.status
  if (!status) {
    return {
      capsules: 'registered-partial',
      selected: 'registered-partial',
      ai: 'registered-partial',
      score: 0,
      sectionLinkAction: 'profile',
    }
  }

  const score = status.score
  const next = status.next_action

  if (next == null) {
    return {
      capsules: 'profile-complete',
      selected: 'profile-complete',
      ai: 'profile-complete',
      score,
      sectionLinkAction: null,
    }
  }

  if (next === 'style_quiz') {
    return {
      capsules: 'newcomer',
      selected: 'newcomer',
      ai: 'newcomer',
      score,
      sectionLinkAction: 'taste',
    }
  }

  if (next === 'measurements') {
    return {
      capsules: 'registered-partial',
      selected: 'registered-partial',
      ai: 'explorer',
      score,
      sectionLinkAction: 'profile',
    }
  }

  // interactions | search_history — deeper partial
  return {
    capsules: 'registered-partial',
    selected: 'registered-partial',
    ai: 'advanced',
    score,
    sectionLinkAction: 'profile',
  }
}
